import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, track, fileUrl, SPECIALTY_EXCLUDE_IDS } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useBooking } from "@/context/BookingContext";
import { Button } from "@/components/ui/button";
import DishCard from "@/components/DishCard";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { Sparkles, Leaf, ChefHat, ChevronLeft, CalendarCheck, ArrowRight } from "lucide-react";

const TABS = [
  { key: "antipasti", label: "Antipasti", cats: ["antipasti"] },
  { key: "primi", label: "Primi", cats: ["primi"] },
  { key: "secondi", label: "Secondi", cats: ["secondi", "brace"] },
  { key: "contorni", label: "Contorni", cats: ["contorni"] },
  { key: "formaggi", label: "Formaggi", cats: ["formaggi"] },
  { key: "dolci", label: "Dolci", cats: ["dolci"] },
  { key: "vini", label: "Vini e bevande", cats: ["vini"] },
];
const SUB_LABELS = { brace: "Dalla brace", secondi: "Dalla cucina" };

const MiniBadges = ({ d }) => (
  <>
    {d.specialita && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-bordeaux/10 text-bordeaux"><Sparkles className="w-3 h-3" />Specialità</span>}
    {d.stagionale && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-green-700/10 text-green-800"><Leaf className="w-3 h-3" />Stagionale</span>}
    {d.fatto_in_casa && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-brass/15 text-brass-hover"><ChefHat className="w-3 h-3" />Fatto in casa</span>}
  </>
);

const MenuRow = ({ d }) => (
  <div className="flex justify-between items-baseline gap-4 py-3 border-b border-brass/10 break-inside-avoid" data-testid={`menurow-${d.id}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-serif text-lg text-oak leading-tight">{d.nome}</h4>
        <MiniBadges d={d} />
      </div>
      {d.descrizione && <p className="font-accent italic text-oak/60 text-sm leading-snug mt-0.5">{d.descrizione}</p>}
      {d.allergeni && <p className="text-[11px] text-oak/45 mt-0.5">Allergeni: {d.allergeni}</p>}
    </div>
    {d.prezzo ? <span className="font-serif text-lg text-bordeaux whitespace-nowrap">€ {d.prezzo}</span> : null}
  </div>
);

const PrenotaCTA = ({ onClick, variant = "band" }) => {
  if (variant === "band") {
    return (
      <div className="my-10 flex flex-col sm:flex-row items-center justify-center gap-4 bg-cream-dark border-y border-brass/25 py-5 px-6">
        <p className="font-accent italic text-lg text-oak text-center">La cucina bolognese, a tavola dal 1961</p>
        <Button onClick={onClick} className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-6 shrink-0" data-testid="menu-cta-prenota"><CalendarCheck className="w-4 h-4 mr-2" />Prenota un tavolo</Button>
      </div>
    );
  }
  return (
    <div className="text-center mt-12">
      <Button onClick={onClick} className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-8 h-12" data-testid="menu-cta-prenota-bottom"><CalendarCheck className="w-4 h-4 mr-2" />Prenota un tavolo</Button>
    </div>
  );
};

export default function Menu() {
  const { settings } = useSettings();
  const { open: openBooking } = useBooking();
  const [dishes, setDishes] = useState([]);
  const [selected, setSelected] = useState(null); // null = overview
  const [searchParams] = useSearchParams();
  const piattoId = searchParams.get("piatto");

  useEffect(() => {
    track("page_view", "menu");
    track("menu_view", "page");
    api.get("/dishes").then((r) => setDishes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!piattoId || !dishes.length) return;
    const t = setTimeout(() => document.querySelector(`[data-testid="dish-${piattoId}"]`)?.scrollIntoView({ block: "center" }), 150);
    return () => clearTimeout(t);
  }, [piattoId, dishes.length]);

  const choose = (key) => {
    setSelected(key);
    if (key) track("menu_view", key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Panoramica specialità: escludo "Cesto di crescentine" e "Coppa, salami, prosciutto" (menù completo invariato)
  const specialties = dishes
    .filter((d) => d.specialita && !SPECIALTY_EXCLUDE_IDS.includes(d.id))
    .sort((a, b) => (a.specialita_order ?? 999) - (b.specialita_order ?? 999))
    .slice(0, 12);
  const seasonal = dishes.filter((d) => d.stagionale).sort((a, b) => (a.stagione_order ?? 999) - (b.stagione_order ?? 999));
  const activeTab = TABS.find((t) => t.key === selected);
  const availableTabs = TABS.filter((t) => t.cats.some((c) => dishes.some((d) => d.categoria === c)));

  return (
    <div>
      <SEO title="Il Menù" description="Il menù del Ristorante Masetti: antipasti, primi di pasta fresca, secondi alla brace, contorni, formaggi, dolci e vini dei Colli Bolognesi." />
      <div className="bg-oak-dark text-cream py-14 text-center">
        <p className="font-accent italic text-brass text-lg">Cucina tipica bolognese dal 1961</p>
        <h1 className="font-serif text-4xl sm:text-5xl">Il Menù</h1>
        {settings.menu_coperto && <p className="text-cream/70 text-sm mt-3">{settings.menu_coperto}</p>}
      </div>

      {/* CATEGORY NAV */}
      <div className="sticky top-16 sm:top-20 z-30 bg-cream/95 backdrop-blur border-b border-brass/20">
        <div className="max-w-7xl mx-auto px-2 overflow-x-auto no-scrollbar">
          <div className="flex gap-1 py-2 min-w-max items-center">
            <button onClick={() => choose(null)} data-testid="menu-tab-overview"
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${selected === null ? "bg-bordeaux text-cream" : "text-oak/70 hover:bg-brass/10"}`}>
              Panoramica
            </button>
            <span className="w-px h-5 bg-brass/25 mx-1" />
            {availableTabs.map((t) => (
              <button key={t.key} onClick={() => choose(t.key)} data-testid={`menu-tab-${t.key}`}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${selected === t.key ? "bg-bordeaux text-cream" : "text-oak/70 hover:bg-brass/10"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* ===== OVERVIEW ===== */}
        {selected === null && (
          <div className="fade-up">
            <p className="text-center text-oak/60 max-w-2xl mx-auto mb-12">
              Una selezione dei nostri piatti più rappresentativi. Tocca una categoria qui sopra per sfogliare il menù completo.
            </p>

            {specialties.length > 0 && (
              <section className="mb-4">
                <Reveal direction="up">
                  <div className="text-center mb-8">
                    <p className="font-accent italic text-brass-hover text-lg">Da non perdere</p>
                    <h2 className="font-serif text-3xl sm:text-4xl text-bordeaux">Le specialità Masetti</h2>
                  </div>
                </Reveal>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {specialties.map((d, i) => (
                    <Reveal key={d.id} direction="up" delay={i * 80}><DishCard d={d} autoOpen={d.id === piattoId} /></Reveal>
                  ))}
                </div>
              </section>
            )}

            <PrenotaCTA onClick={() => openBooking("menu-overview")} />

            {seasonal.length > 0 && (
              <section className="mb-4">
                <Reveal direction="up">
                  <div className="text-center mb-8">
                    <p className="font-accent italic text-brass-hover text-lg">Il meglio del momento</p>
                    <h2 className="font-serif text-3xl sm:text-4xl text-bordeaux">Profumi di stagione</h2>
                  </div>
                </Reveal>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {seasonal.map((d, i) => (
                    <Reveal key={d.id} direction="up" delay={i * 80}><DishCard d={d} autoOpen={d.id === piattoId} /></Reveal>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-16 text-center">
              <h3 className="font-serif text-2xl text-bordeaux mb-6">Sfoglia il menù completo</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {availableTabs.map((t) => (
                  <button key={t.key} onClick={() => choose(t.key)} data-testid={`menu-overviewchip-${t.key}`}
                    className="group flex items-center gap-2 bg-white border border-brass/25 rounded-full px-5 py-2.5 hover:border-brass hover:shadow-sm transition-all">
                    <span className="font-serif text-oak group-hover:text-bordeaux">{t.label}</span>
                    <ArrowRight className="w-4 h-4 text-brass" />
                  </button>
                ))}
              </div>
            </div>

            {settings.menu_note && (
              <p className="text-xs text-oak/50 italic border-t border-brass/15 pt-6 mt-14 text-center">{settings.menu_note}</p>
            )}
          </div>
        )}

        {/* ===== CATEGORY DETAIL ===== */}
        {activeTab && (
          <div className="fade-up">
            <button onClick={() => choose(null)} data-testid="menu-back" className="inline-flex items-center gap-1 text-sm text-oak/70 hover:text-bordeaux mb-4">
              <ChevronLeft className="w-4 h-4" />Torna alla panoramica
            </button>
            <h2 className="font-serif text-3xl sm:text-4xl text-bordeaux mb-1">{activeTab.label}</h2>
            <div className="section-divider w-full mb-8" />

            {activeTab.cats.map((cat) => {
              const items = dishes.filter((d) => d.categoria === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="mb-10">
                  {activeTab.cats.length > 1 && <h3 className="font-serif text-xl text-brass-hover mb-2">{SUB_LABELS[cat] || cat}</h3>}
                  <div className="md:columns-2 md:gap-x-12">
                    {items.map((d) => <MenuRow key={d.id} d={d} />)}
                  </div>
                </div>
              );
            })}

            <PrenotaCTA variant="bottom" onClick={() => openBooking(`menu-${activeTab.key}`)} />

            {settings.menu_note && (
              <p className="text-xs text-oak/50 italic border-t border-brass/15 pt-6 mt-12 text-center">{settings.menu_note}</p>
            )}
          </div>
        )}

        {dishes.length === 0 && <p className="text-center text-oak/60 py-20">Menù in aggiornamento.</p>}
      </div>
    </div>
  );
}
